import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Smartphone, Tablet, Share, Plus, MoreVertical, Download } from "lucide-react";
import { useBrowserDetection } from "@/hooks/use-browser-detection";

interface PWAInstallModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

export function PWAInstallModal({ isOpen, onOpenChange }: PWAInstallModalProps) {
  const { isChrome } = useBrowserDetection();

  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" />
            Add JamControl to Your Home Screen
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-6 pr-2">
          <div>
            <p className="text-sm text-slate-600 mb-4">
              Install JamControl as a Progressive Web App (PWA) for the best experience! 
              You'll get faster loading, push notifications, and app-like navigation.
            </p>
          </div>

          {/* iOS Instructions */}
          {(isIOS || !isMobile) && (
            <div className="border border-blue-200 rounded-lg p-4 bg-blue-50/50">
              <h3 className="font-semibold text-lg mb-3 flex items-center gap-2 text-blue-800">
                <Smartphone className="h-4 w-4" />
                iOS (iPhone/iPad) - Safari
              </h3>
              <ol className="text-sm text-slate-700 space-y-3 ml-4 list-decimal">
                <li>
                  <strong>Open Safari</strong> and navigate to JamControl (you're here now!)
                </li>
                <li className="flex items-start gap-2">
                  <span>Tap the <strong>Share</strong> button</span>
                  <Share className="h-4 w-4 mt-0.5 text-blue-600"/>
                  <span>at the bottom of the screen</span>
                </li>
                <li>
                  <span>Scroll down and tap </span>
                  <strong className="inline-flex items-center gap-1">
                    "Add to Home Screen"
                    <Plus className="h-3 w-3"/>
                  </strong>
                </li>
                <li>
                  <strong>Customize the name</strong> (defaults to "JamControl") and tap <strong>"Add"</strong>
                </li>
                <li>
                  <strong>Done!</strong> JamControl will now appear on your home screen like a native app
                </li>
                <li>
                  <strong>Enable notifications</strong> - you can activate them from your account settings (this has to be done from the installed app)
                </li>
              </ol>
            </div>
          )}

          {/* Android Instructions */}
          {(isAndroid || !isMobile) && (
              <div className="border border-green-200 rounded-lg p-4 bg-green-50/50">
              <h3 className="font-semibold text-lg mb-3 flex items-center gap-2 text-green-800">
                <Tablet className="h-4 w-4" />
                Android - Chrome
              </h3>
                <ol className="text-sm text-slate-700 space-y-3 ml-4 list-decimal">
                  <li>
                    <strong>Open Chrome</strong> and navigate to JamControl (you're here now!)
                  </li>
                  <li className="flex items-start gap-2">
                    <span>Tap the <strong>menu</strong> button</span>
                    <MoreVertical className="h-4 w-4 mt-0.5 text-green-600"/>
                    <span>in the top right corner</span>
                  </li>
                  <li>
                    <span>Look for </span>
                    <strong className="inline-flex items-center gap-1">
                      "Add to Home screen" or "Install app"
                      <Download className="h-3 w-3"/>
                    </strong>
                  </li>
                  <li>
                    <strong>Tap "Add"</strong> or <strong>"Install"</strong> in the confirmation dialog
                  </li>
                  <li>
                    <strong>Done!</strong> JamControl will now appear on your home screen and in your app drawer
                  </li>
                  <li>
                    To enable proper notifications, you can activate them from your account settings (do this from the app added to your home screen)
                  </li>
                </ol>
                <div className="mt-3 p-3 bg-green-100 rounded-lg">
                  <p className="text-sm text-green-800">
                    <strong>Pro tip:</strong> Some Android devices may show an automatic "Install app" banner at the bottom of the screen - just tap it!
                </p>
              </div>
            </div>
          )}

          {/* Benefits */}
          <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
            <h3 className="font-semibold text-lg mb-3 text-slate-800">
              Why Install as a PWA?
            </h3>
            <ul className="text-sm text-slate-600 space-y-2 ml-4 list-disc">
              <li><strong>Faster loading</strong> - App resources are cached locally</li>
              <li><strong>App-like experience</strong> - No browser UI, dedicated window</li>
              <li><strong>Push notifications</strong> - Get real-time updates about your events</li>
              <li><strong>Quick access</strong> - Launch directly from your home screen</li>
            </ul>
          </div>

        </div>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} variant="outline">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}